import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormField, Image } from '@components/common';
import { Button } from 'react-bootstrap';
import { isEqual } from 'lodash';
import utils, { constants } from '@utils';

class CreditCardForm extends Component {
	constructor(props) {
		super(props);

		this.state = this.initialState(props);

		this.fieldStyle = {
			fieldCol   : 12,
			labelCol   : 12,
			labelClass : 'margin-bottom-5 text-left',
			groupClass : 'margin-bottom-10',
		};
	}

	shouldComponentUpdate({ form }, { editAddress, cardFormat }) {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(form, lastProps.form)
			|| !isEqual(editAddress, lastState.editAddress)
			|| !isEqual(cardFormat, lastState.cardFormat)
		);
	}

	initialState = (props) => ({
		editAddress : props.editAddress || false,
		cardFormat  : {
			type           : '',
			cardNumberMask : constants.MASK_VALUE.creditCardMask,
			cvcMask        : constants.MASK_VALUE.cvcMask,
			cardImageSrc   : '/images/cardIcons/default.png',
		},
	})

	buildSummary = () => {
		const {
			form: {
				address, city, state, zip,
			}, USStates,
		} = this.props;

		const addressState = USStates.find((item) => item.value === state.value) || {};
		const displayAddress = `${address.value}, ${city.value}, ${addressState.label}, ${zip.value}`;

		if (address.value && city.value && addressState.label && zip.value) return (<p className="font-light">{displayAddress}</p>);

		return '';
	}

	toggleAddressFields = () => this.setState(((prevState) => ({ editAddress : !prevState.editAddress })));

	onCreditCardNumberChange = (event) => {
		const { onChange } = this.props;
		const { cardFormat: { type: cardType } } = this.state;

		onChange('cardNumber', 'field')(event);

		// Remove white space from the input to get only the card numbers
		const cleanValue = event.target.value.replace(/\s+/g, '');
		const { card } = utils.processCardNumber(cleanValue);

		// Only change the card format if the type is different
		if (card && (card.type !== cardType)) {
			const newCardFormat = {
				type           : card.type,
				cardNumberMask : utils.buildCardNumberMask(card),
				cvcMask        : utils.buildCvvMask(card),
				cardImageSrc   : constants.CARD_ICONS[card.type] || constants.CARD_ICONS.default,
			};

			this.setState({ cardFormat : newCardFormat });
		}
	}

	render() {
		const { editAddress, cardFormat: { cardNumberMask, cvcMask, cardImageSrc } } = this.state;
		const {
			form, USStates, loadingUSStates, onChange,
		} = this.props;

		return (
			<div className="form-horizontal">
				<div>
					<FormField
						fieldStyle={this.fieldStyle}
						label="Card Holder Full Name"
						name="cardHolderName"
						value={form.cardHolderName.value}
						validation={form.cardHolderName.validation}
						onChange={onChange('cardHolderName', 'field')}
					/>
				</div>
				<div className="clearfix" />
				<div className="flex flex-align-items-bottom">
					<div className="flex-auto margin-right-10">
						<FormField
							fieldStyle={this.fieldStyle}
							controlType="masked"
							mask={cardNumberMask}
							label="Card Number"
							name="cardNumber"
							value={form.cardNumber.value}
							validation={form.cardNumber.validation}
							onChange={this.onCreditCardNumberChange}
						/>
					</div>
					<div className="margin-bottom-16">
						<Image
							className="height-22"
							src={cardImageSrc}
						/>
					</div>
				</div>
				<div className="clearfix" />
				<div className="flex flex-align-items-bottom">
					<div className="flex-1 margin-right-20">
						<FormField
							fieldStyle={this.fieldStyle}
							controlType="masked"
							mask={constants.MASK_VALUE.dateExpiryMask}
							label="Expiry Date (MM/YY)"
							name="expiryDate"
							value={form.expiryDate.value}
							validation={form.expiryDate.validation}
							onChange={onChange('expiryDate', 'field')}
						/>
					</div>
					<div className="flex-1">
						<FormField
							fieldStyle={this.fieldStyle}
							controlType="masked"
							mask={cvcMask}
							label="CVC / CVV"
							name="cvc"
							value={form.cvc.value}
							validation={form.cvc.validation}
							onChange={onChange('cvc', 'field')}
						/>
					</div>
				</div>
				<div className="separator separator-dashed" />
				<div className="margin-top-15">
					<div className="flex flex-align-items-center">
						<div className="flex-auto">
							<span className="font-bold color-gray-mine-shaft font-size-16">Billing Address</span>
						</div>
						<div>
							<Button
								bsStyle="link"
								onClick={this.toggleAddressFields}
							>
								<i className={`margin-right-10 rkf ${editAddress ? 'rkf-chevron-up' : 'rkf-chevron-down'}`} />
								Edit Address
							</Button>
						</div>
					</div>
				</div>
				<div className="margin-top-10">
					<Choose>
						<When condition={!editAddress}>
							{this.buildSummary()}
						</When>
						<Otherwise>
							<div>
								<FormField
									fieldStyle={this.fieldStyle}
									label="Address"
									name="address"
									value={form.address.value}
									validation={form.address.validation}
									onChange={onChange('address', 'field')}
								/>
							</div>
							<div>
								<FormField
									fieldStyle={this.fieldStyle}
									label="City"
									name="city"
									value={form.city.value}
									validation={form.city.validation}
									onChange={onChange('city', 'field')}
								/>
							</div>
							<div className="flex flex-align-items-center">
								<div className="flex-1 margin-right-20">
									<FormField
										fieldStyle={this.fieldStyle}
										controlType="select"
										options={USStates}
										loading={loadingUSStates}
										placeholder="Select..."
										label="State"
										name="state"
										value={form.state.value}
										validation={form.state.validation}
										onChange={onChange('state', 'select')}
									/>
								</div>
								<div className="flex-1">
									<FormField
										fieldStyle={this.fieldStyle}
										label="Zip"
										name="zip"
										value={form.zip.value}
										validation={form.zip.validation}
										onChange={onChange('zip', 'field')}
									/>
								</div>
							</div>
						</Otherwise>
					</Choose>
				</div>
			</div>
		);
	}
}

CreditCardForm.propTypes = {
	USStates        : PropTypes.array,
	form            : PropTypes.object.isRequired,
	loadingUSStates : PropTypes.bool,
	onChange        : PropTypes.func.isRequired,
};

CreditCardForm.defaultProps = {
	USStates        : [],
	loadingUSStates : false,
};

export default CreditCardForm;
